import React from 'react';
import { Card, Space, Select, Button, Tabs, Table, Tag, Typography, DatePicker, Radio } from 'antd';
import BatteryChartOptimized from '../charts/BatteryChartOptimized';
import NetworkChart from '../charts/NetworkChart';
import { BatteryHistoryItem } from '../../types/api';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

const { Text } = Typography;

interface NetworkHistoryItem {
  timestamp: string;
  status: string;
  connection_type?: string;
  wifi_ssid?: string;
  wifi_signal_strength?: number;
  wifi_security_type?: string;
  cellular_carrier?: string;
  cellular_network_type?: string;
  cellular_signal_strength?: number;
  ip_address?: string;
  is_metered?: boolean;
  metadata?: any;
}



interface DeviceHistoryProps {
  networkHistory: NetworkHistoryItem[];
  batteryHistory: BatteryHistoryItem[];
  historyLoading: boolean;
  historyDays: number;
  historyError: string | null;
  onHistoryDaysChange: (days: number) => void;
  onDateRangeChange?: (fromTime: string, toTime: string) => void;
  onLoadHistory: () => void;
}

const DeviceHistory: React.FC<DeviceHistoryProps> = ({
  networkHistory,
  batteryHistory,
  historyLoading,
  historyDays,
  historyError,
  onHistoryDaysChange,
  onDateRangeChange,
  onLoadHistory,
}) => {
  const [pageSize, setPageSize] = React.useState(50);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [highlightedTimestamp, setHighlightedTimestamp] = React.useState<string | null>(null);
  const [chartControlled, setChartControlled] = React.useState(false);
  const [chartRangeData, setChartRangeData] = React.useState<any[]>([]);
  const [customInputError, setCustomInputError] = React.useState<string | null>(null);
  const [periodMode, setPeriodMode] = React.useState<'preset' | 'custom'>('preset');
  const [dateRange, setDateRange] = React.useState<[Dayjs, Dayjs] | null>(null);
  
  const tableData = React.useMemo(() => {
    const sortedData = [...batteryHistory].sort((a, b) => 
      new Date(b.measured_at).getTime() - new Date(a.measured_at).getTime()
    );
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [batteryHistory, currentPage, pageSize]);

  const displayChartData = React.useMemo(() => {
    if (chartControlled) {
      return batteryHistory; // Chart needs all data to allow panning/zooming
    }
    return tableData;
  }, [chartControlled, batteryHistory, tableData]);

  const displayTableData = React.useMemo(() => {
    if (chartControlled) {
      return chartRangeData.sort((a, b) => new Date(b.measured_at).getTime() - new Date(a.measured_at).getTime());
    }
    return tableData;
  }, [chartControlled, chartRangeData, tableData]);
  
  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
    setChartControlled(false);
  }, []);
  
  const handlePageSizeChange = React.useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    setChartControlled(false);
  }, []);

  const handleChartRangeChange = React.useCallback((rangeData: any[]) => {
    setChartRangeData(rangeData);
    setChartControlled(true);
  }, []);
  return (
    <Card title={<span style={{ fontSize: '12px' }}>デバイス履歴</span>} size="small">
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Radio.Group 
              value={periodMode} 
              onChange={(e) => setPeriodMode(e.target.value)}
            >
              <Radio.Button value="preset">プリセット期間</Radio.Button>
              <Radio.Button value="custom">カスタム期間</Radio.Button>
            </Radio.Group>
            {periodMode === 'preset' ? (
              <Select
                value={historyDays}
                onChange={onHistoryDaysChange}
                style={{ width: 120 }}
                options={[
                  { label: '1日', value: 1 },
                  { label: '3日', value: 3 },
                  { label: '7日', value: 7 },
                  { label: '30日', value: 30 },
                  { label: '90日', value: 90 }
                ]}
              />
            ) : (
              <RangePicker
                showTime
                value={dateRange}
                onChange={(dates) => {
                  setDateRange(dates);
                  if (dates && dates[0] && dates[1] && onDateRangeChange) {
                    onDateRangeChange(dates[0].toISOString(), dates[1].toISOString());
                  }
                }}
                placeholder={['開始日時', '終了日時']}
                style={{ width: 280 }}
              />
            )}
          </Space>
          <Button onClick={onLoadHistory} loading={historyLoading}>
            更新
          </Button>
        </div>
      </div>
      
      <Tabs
        onChange={(key) => {
          const shouldLoadHistory = (key === 'battery' && batteryHistory.length === 0) || 
                                   (key === 'network' && networkHistory.length === 0);
          if (shouldLoadHistory) {
            onLoadHistory();
          }
        }}
        items={[
          {
            key: 'network',
            label: 'ネットワーク状態',
            children: (
              <div>
                <div style={{ marginBottom: 24 }}>
                  {networkHistory.length === 0 && !historyLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', color: '#999' }}>
                      データがありません
                    </div>
                  ) : (
                    <NetworkChart data={networkHistory} height={300} />
                  )}
                </div>
                
                <Table
                  dataSource={networkHistory}
                  rowKey={(record: NetworkHistoryItem) => record.timestamp}
                  size="small"
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  columns={[
                    {
                      title: '時刻',
                      dataIndex: 'timestamp',
                      render: (timestamp) => new Date(timestamp).toLocaleString(),
                      width: 150
                    },
                    {
                      title: '状態',
                      dataIndex: 'status',
                      render: (status) => (
                        <Tag color={status === 'online' ? 'green' : 'red'}>
                          {status === 'online' ? 'オンライン' : 'オフライン'}
                        </Tag>
                      ),
                      width: 80
                    },
                    {
                      title: '接続タイプ',
                      dataIndex: 'connection_type',
                      render: (type) => type ? (
                        <Tag color={type === 'wifi' ? 'blue' : 'orange'}>
                          {type === 'wifi' ? 'Wi-Fi' : 'セルラー'}
                        </Tag>
                      ) : '-',
                      width: 100
                    },
                    {
                      title: 'Wi-Fi SSID',
                      dataIndex: 'wifi_ssid',
                      render: (ssid) => ssid || '-',
                      width: 120
                    },
                    {
                      title: '信号強度',
                      render: (record: NetworkHistoryItem) => {
                        if (record.wifi_signal_strength) {
                          return `${record.wifi_signal_strength} dBm`;
                        }
                        if (record.cellular_signal_strength) {
                          return `${record.cellular_signal_strength} dBm`;
                        }
                        return '-';
                      },
                      width: 100
                    }
                  ]}
                />
              </div>
            )
          },
          {
            key: 'battery',
            label: 'バッテリー状態',
            children: (
              <div>
                {historyError && (
                  <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 6 }}>
                    <Text type="danger">{historyError}</Text>
                  </div>
                )}
                <div style={{ marginBottom: 24 }}>
                  {batteryHistory.length === 0 && !historyLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', color: '#999' }}>
                      データがありません
                    </div>
                  ) : (
                    <BatteryChartOptimized 
                      data={displayChartData}
                      allData={batteryHistory}
                      height={300} 
                      highlightedTimestamp={highlightedTimestamp}
                      onHighlightChange={setHighlightedTimestamp}
                      onRangeChange={handleChartRangeChange}
                      autoFit={!chartControlled}
                    />
                  )}
                </div>
                
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space direction="vertical" size={4}>
                    <Space>
                      <span>表示行数:</span>
                      <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        style={{ width: 120 }}
                        dropdownRender={(menu) => (
                          <div>
                            {menu}
                            <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                              <input
                                type="number"
                                min="10"
                                max="1000"
                                placeholder="カスタム値 (10-1000)"
                                style={{
                                  width: '100%',
                                  padding: '4px 8px',
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const value = parseInt(e.currentTarget.value);
                                    if (isNaN(value) || value < 10 || value > 1000) {
                                      setCustomInputError('10から1000の範囲で入力してください');
                                      setTimeout(() => setCustomInputError(null), 3000);
                                      e.preventDefault();
                                    } else {
                                      setCustomInputError(null);
                                      handlePageSizeChange(value);
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                        options={[
                          { label: '10', value: 10 },
                          { label: '20', value: 20 },
                          { label: '50', value: 50 },
                          { label: '100', value: 100 },
                          { label: '200', value: 200 }
                        ]}
                      />
                      <Text type="secondary" style={{ fontSize: '11px', color: '#999', marginLeft: '12px' }}>
                        全{batteryHistory.length}件
                        {displayTableData.length > 0 && (
                          <span> | {new Date(displayTableData[0].measured_at.replace(/Z$/, '')).toLocaleString()} ～ {new Date(displayTableData[displayTableData.length - 1].measured_at.replace(/Z$/, '')).toLocaleString()}</span>
                        )}
                      </Text>
                    </Space>
                    {customInputError && (
                      <div style={{
                        fontSize: '11px',
                        color: '#ff4d4f',
                        marginLeft: '60px'
                      }}>
                        {customInputError}
                      </div>
                    )}
                  </Space>
                  <div>
                    <Button
                      size="small"
                      disabled={currentPage >= Math.ceil(batteryHistory.length / pageSize)}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      古いデータ
                    </Button>
                    <span style={{ margin: '0 8px' }}>
                      {currentPage} / {Math.ceil(batteryHistory.length / pageSize)}
                    </span>
                    <Button
                      size="small"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      新しいデータ
                    </Button>
                  </div>
                </div>
                
                <Table
                  dataSource={displayTableData}
                  rowKey={(record: BatteryHistoryItem) => record.measured_at}
                  size="small"
                  pagination={false}
                  scroll={{ y: 400 }}
                  onRow={(record) => ({
                    onMouseEnter: () => setHighlightedTimestamp(record.measured_at),
                    onMouseLeave: () => setHighlightedTimestamp(null),
                  })}
                  columns={[
                    {
                      title: '時刻',
                      dataIndex: 'measured_at',
                      render: (measured_at) => {
                        const date = typeof measured_at === 'number' ? new Date(measured_at * 1000) : new Date(measured_at.replace(/Z$/, ''));
                        return date.toLocaleString();
                      },
                      width: 150
                    },
                    {
                      title: 'バッテリー残量',
                      dataIndex: 'battery_level',
                      render: (level) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ 
                            width: 60, 
                            height: 8, 
                            backgroundColor: '#f0f0f0', 
                            borderRadius: 4,
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${level}%`,
                              height: '100%',
                              backgroundColor: level > 20 ? '#52c41a' : level > 10 ? '#faad14' : '#ff4d4f',
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <span>{level}%</span>
                        </div>
                      ),
                      width: 120
                    },
                    {
                      title: '充電状態',
                      dataIndex: 'charging_status',
                      render: (status) => {
                        const statusMap: Record<string, { color: string; text: string }> = {
                          charging: { color: 'green', text: '充電中' },
                          discharging: { color: 'orange', text: '放電中' },
                          full: { color: 'blue', text: '満充電' },
                          critical: { color: 'red', text: '低電力' }
                        };
                        const config = statusMap[status] || { color: 'default', text: status };
                        return <Tag color={config.color}>{config.text}</Tag>;
                      },
                      width: 100
                    },
                    {
                      title: '温度',
                      dataIndex: 'temperature_celsius',
                      render: (temp) => temp ? `${temp}℃` : '-',
                      width: 80
                    },
                    {
                      title: '予測残時間',
                      dataIndex: 'remaining_time_minutes',
                      render: (minutes: number | undefined) => {
                        if (!minutes) return '-';
                        const hours = Math.floor(minutes / 60);
                        const mins = minutes % 60;
                        return `${hours}時間${mins}分`;
                      },
                      width: 100
                    },
                    {
                      title: '実効容量',
                      dataIndex: 'capacity_mah',
                      render: (capacity) => capacity ? `${capacity} mAh` : '-',
                      width: 100
                    }
                  ]}
                />
              </div>
            )
          }
        ]}
      />
    </Card>
  );
};

export default DeviceHistory;